import { inject, Injectable, signal } from '@angular/core';
import { Rum } from '@insolution/audit-data-access';
import { Apollo } from 'apollo-angular';
import {
  AuthUser,
  confirmResetPassword,
  ConfirmResetPasswordInput,
  confirmSignIn,
  ConfirmSignInOutput,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  ResetPasswordOutput,
  signIn,
  SignInOutput,
  signOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apollo = inject(Apollo);
  private rum = inject(Rum);

  public isLoggedIn = signal<boolean | null>(null);
  public currentUser = signal<AuthUser | null>(null);

  private checkingClaimTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.setIsLoggedIn();

    Hub.listen('auth', () => this.setIsLoggedIn());

    this.handleAuthenticationInOtherBrowserTabs();
  }

  private async setIsLoggedIn() {
    try {
      const currentUser = await getCurrentUser();
      this.isLoggedIn.set(true);
      this.currentUser.set(currentUser);
      this.rum.setIdentity({ actor: currentUser.username, sub: currentUser.userId });
    } catch {
      this.isLoggedIn.set(false);
      this.currentUser.set(null);
    }
  }

  public async login(credentials: { username: string; password: string }): Promise<SignInOutput> {
    const signInResult = await signIn(credentials);

    if (signInResult.isSignedIn) {
      this.isLoggedIn.set(true);
    }

    try {
      const user = await getCurrentUser();
      this.currentUser.set(user);
      this.rum.setIdentity({ actor: user.username, sub: user.userId });
    } catch {
      this.currentUser.set(null);
    }

    return signInResult;
  }

  public async logout(): Promise<void> {
    const signOutResult = await signOut();

    this.isLoggedIn.set(false);
    this.currentUser.set(null);

    this.rum.clearIdentity();

    await this.apollo.client.clearStore();

    return signOutResult;
  }

  public async confirmNewPassword(newPassword: string): Promise<SignInOutput> {
    const confirmResult = await confirmSignIn({
      challengeResponse: newPassword,
    });

    if (confirmResult.isSignedIn) {
      this.isLoggedIn.set(true);

      try {
        const user = await getCurrentUser();
        this.currentUser.set(user);
        this.rum.setIdentity({ actor: user.username, sub: user.userId });
      } catch {
        this.currentUser.set(null);
      }
    }

    return confirmResult;
  }

  public resetPassword(username: string): Promise<ResetPasswordOutput> {
    return resetPassword({ username });
  }

  public confirmResetPassword({ username, confirmationCode, newPassword }: ConfirmResetPasswordInput): Promise<void> {
    return confirmResetPassword({ username, confirmationCode, newPassword });
  }

  public async confirmMFA(totpCode: string): Promise<ConfirmSignInOutput> {
    const confirmResult = await confirmSignIn({ challengeResponse: totpCode });

    if (confirmResult.isSignedIn) {
      this.isLoggedIn.set(true);

      try {
        const user = await getCurrentUser();
        this.currentUser.set(user);
        this.rum.setIdentity({ actor: user.username, sub: user.userId });
      } catch {
        this.currentUser.set(null);
      }
    }

    return confirmResult;
  }

  public waitForLoginStatus(): Promise<boolean> {
    // use this method to wait until the login status is known (not null)
    // this is useful for route guards on page refresh and initial app load

    return new Promise((resolve) => {
      const checkStatus = () => {
        const isLoggedIn = this.isLoggedIn();

        if (isLoggedIn !== null) {
          resolve(isLoggedIn);
        } else {
          setTimeout(checkStatus, 100); // Check opnieuw na 100ms
        }
      };

      checkStatus();
    });
  }

  private handleAuthenticationInOtherBrowserTabs(): void {
    window.addEventListener('storage', (event) => {
      if (event.key?.includes('CognitoIdentityServiceProvider')) {
        if (
          event.key.includes('.idToken') ||
          event.key.includes('.accessToken') ||
          event.key.includes('.refreshToken') ||
          event.key.includes('.LastAuthUser')
        ) {
          if (event.newValue) {
            this.checkClaim();
          } else {
            window.location.reload();
          }
        }
      }
    });
  }

  private checkClaim(): void {
    // prevent triggering when multiple events trigger (which is almost always the case when tokens are updated in local storage)
    if (this.checkingClaimTimer) {
      clearTimeout(this.checkingClaimTimer);
    }

    this.checkingClaimTimer = setTimeout(async () => {
      try {
        const tenantRefFromClaim = await this.getTenantRefFromClaim();
        if (!tenantRefFromClaim) {
          console.warn('No tenant_ref claim found in idToken');
          window.location.reload();
          return;
        }

        const tenantRefFromSession = this.getTenantRefFromSession();
        if (!tenantRefFromSession) {
          console.warn('No currentTenant found in session storage');
          window.location.reload();
          return;
        }

        if (tenantRefFromSession.startsWith(tenantRefFromClaim)) {
          console.log('Tenant ref matches, no action needed');
          return;
        }

        console.warn('Tenant ref mismatch detected, reloading page to select a tenant that user has access to');
        window.location.reload();
      } catch (error) {
        console.error('Error checking claim:', error);
      }
    }, 100);
  }

  private async getTenantRefFromClaim(): Promise<string | null> {
    const idToken = await this.getIdToken();
    if (!idToken) {
      return null;
    }

    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return payload['custom:tenant_ref'] || null;
  }

  private async getIdToken(): Promise<string | null> {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  }

  private getTenantRefFromSession(): string | null {
    const tenantFromSession = sessionStorage.getItem('currentTenant');
    return tenantFromSession ? JSON.parse(tenantFromSession).tenant_ref : null;
  }
}
